import * as React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Activity, Loader, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip';
import { Button } from 'components/ui/button';
import type { ViagemControle } from '../types';
import { DataTableHelper, Formatter } from 'utils';
import { formatViagemCurrency } from '../utils';

const dt = new DataTableHelper<ViagemControle>();

type Props = {
  onApprove?: (id: number) => void;
  onReject?: (viagem: ViagemControle) => void;
  onShowActivities?: (viagem: ViagemControle) => void;
  canApprove?: boolean;
  canReject?: boolean;
  actionLoadingId?: number | null;
  showActions?: boolean;
  showApprovalStatus?: boolean;
};

const formatDate = (value: string | null) => {
  return value ? Formatter.date(value, { format: 'dd/MM/yyyy HH:mm' }) : '---';
};

const formatTime = (value: string | null) => {
  return value || '---';
};

const getApprovalTooltip = (row: ViagemControle) => {
  if (row.rejectedAt) {
    return {
      action: 'Reprovado por',
      user: row.rejectedByUser,
      date: row.rejectedAt,
    };
  }

  if (row.approvedAt) {
    return {
      action: 'Aprovado por',
      user: row.approvedByUser,
      date: row.approvedAt,
    };
  }

  if (row.billingItem) {
    return {
      action: 'Fatura',
      user: null,
      date: null,
      billingId: row.billingItem.viagemBillingId,
    };
  }

  return null;
};

export const useViagensControleColumns = ({
  onApprove,
  onReject,
  onShowActivities,
  canApprove = false,
  canReject = false,
  actionLoadingId,
  showActions = false,
  showApprovalStatus = false,
}: Props): ColumnDef<ViagemControle>[] => {
  return React.useMemo(() => {
    const idViagem = dt.buildColumn({
      id: 'idViagem',
      title: 'ID viagem',
      accessorKey: 'idViagem',
      content: (row) => row.idViagem || '---',
    });
    const shipment = dt.buildColumn({
      id: 'shipment',
      title: 'Shipment',
      accessorKey: 'shipment',
      content: (row) => row.shipment || '---',
    });
    const notas = dt.buildColumn({
      id: 'notas',
      title: 'Nota',
      accessorKey: 'notas',
      content: (row) => row.notas || '---',
    });
    const conhecimento = dt.buildColumn({
      id: 'conhecimento',
      title: 'Conhecimento',
      accessorKey: 'conhecimento',
      content: (row) => row.conhecimento || '---',
    });
    const viagem = dt.buildColumn({
      id: 'viagem',
      title: 'Viagem',
      accessorKey: 'viagem',
      content: (row) => row.viagem || '---',
    });
    const placa = dt.buildColumn({
      id: 'placa',
      title: 'Placa',
      accessorKey: 'placa',
      content: (row) => row.placa || '---',
    });
    const agendamento = dt.buildColumn({
      id: 'agendamento',
      title: 'Agendamento',
      accessorKey: 'agendamento',
      content: (row) => formatDate(row.agendamento),
    });
    const chegada = dt.buildColumn({
      id: 'chegada',
      title: 'Chegada',
      accessorKey: 'chegada',
      content: (row) => formatDate(row.chegada),
    });
    const fimDescarregamento = dt.buildColumn({
      id: 'fimDescarregamento',
      title: 'Fim descarregamento',
      accessorKey: 'fimDescarregamento',
      content: (row) => formatDate(row.fimDescarregamento),
    });
    const permanenciaRec = dt.buildColumn({
      id: 'permanenciaRec',
      title: 'Permanência REC',
      accessorKey: 'permanenciaRec',
      content: (row) => formatTime(row.permanenciaRec),
    });
    const fimCarregamento = dt.buildColumn({
      id: 'fimCarregamento',
      title: 'Fim carregamento',
      accessorKey: 'fimCarregamento',
      content: (row) => formatDate(row.fimCarregamento),
    });
    const saida = dt.buildColumn({
      id: 'saida',
      title: 'Saída',
      accessorKey: 'saida',
      content: (row) => formatDate(row.saida),
    });
    const permanenciaExp = dt.buildColumn({
      id: 'permanenciaExp',
      title: 'Permanência EXP',
      accessorKey: 'permanenciaExp',
      content: (row) => formatTime(row.permanenciaExp),
    });
    const totalPermanencia = dt.buildColumn({
      id: 'totalPermanencia',
      title: 'Total permanência',
      accessorKey: 'totalPermanencia',
      content: (row) => formatTime(row.totalPermanencia),
    });
    const limiteRestante = dt.buildColumn({
      id: 'limiteRestante',
      title: 'Limite restante',
      accessorKey: 'limiteRestante',
      content: (row) => formatTime(row.limiteRestante),
    });
    const estadiaPagar = dt.buildColumn({
      id: 'estadiaPagar',
      title: 'Estadia pagar',
      accessorKey: 'estadiaPagar',
      content: (row) => formatTime(row.estadiaPagar),
    });
    const valorEstadia = dt.buildColumn({
      id: 'valorEstadia',
      title: 'Valor estadia',
      accessorKey: 'valorEstadia',
      content: (row) => formatViagemCurrency(row.valorEstadia),
    });
    const approvalProtocol = dt.buildColumn({
      id: 'approvalProtocol',
      title: 'Protocolo',
      accessorKey: 'approvalProtocol',
      content: (row) => row.approvalProtocol || '---',
    });
    const status = dt.buildColumn({
      id: 'status',
      title: 'Status',
      accessorKey: 'status',
      content: (row) => row.status || '---',
    });
    const approvalStatus = dt.buildColumn({
      id: 'approvalStatus',
      title: 'Aprovação',
      enableSorting: false,
      content: (row) => {
        const isRevalidation = Boolean(row.reversedAt);
        const label = isRevalidation
          ? 'Revalidar'
          : row.rejectedAt
          ? 'Rejeitado'
          : row.approvedAt
            ? 'Aprovado'
            : row.billingItem
              ? 'Faturada'
              : 'Pendente';

        const className = isRevalidation
          ? 'bg-amber-50 text-amber-700 border-amber-200'
          : row.rejectedAt
          ? 'bg-red-50 text-red-700 border-red-200'
          : row.approvedAt
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : row.billingItem
              ? 'bg-blue-50 text-blue-700 border-blue-200'
              : 'bg-slate-50 text-slate-600 border-slate-200';
        const tooltip = getApprovalTooltip(row);
        const badge = (
          <span
            className={`inline-flex rounded-sm border px-2 py-1 text-xs font-GilroySemibold ${className}`}
          >
            {label}
          </span>
        );

        if (!tooltip) return badge;

        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>{badge}</TooltipTrigger>
              <TooltipContent side="top" className="bg-slate-700 text-white">
                <div className="flex flex-col gap-1">
                  {'billingId' in tooltip ? (
                    <span className="font-GilroySemibold">
                      Vinculada à fatura {tooltip.billingId}
                    </span>
                  ) : (
                    <span className="font-GilroySemibold">
                      {tooltip.action}{' '}
                      {tooltip.user?.name || 'Usuário não identificado'}
                    </span>
                  )}
                  {!('billingId' in tooltip) && tooltip.user?.email && (
                    <span className="text-xs text-slate-100">
                      {tooltip.user.email}
                    </span>
                  )}
                  {!('billingId' in tooltip) && tooltip.date && (
                    <span className="text-xs text-slate-100">
                      {formatDate(tooltip.date)}
                    </span>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    });
    const actions = dt.buildColumn({
      id: 'acoes',
      title: 'Ações',
      enableSorting: false,
      enableHiding: false,
      content: (row) => {
        const billing = row.billingItem?.billing;
        const isRevalidation = Boolean(
          row.reversedAt ||
            row.rejectedAt ||
            billing?.rejectedAt ||
            billing?.reversedAt,
        );
        const isBilled = Boolean(row.billingItem) && !isRevalidation;
        const actionLoading = actionLoadingId === row.id;

        if (!canApprove && !canReject && !onShowActivities) {
          return <div className="text-right">---</div>;
        }

        return (
          <DropdownMenu>
            <DropdownMenuTrigger
              asChild
              onClick={(event) => event.stopPropagation()}
            >
              <Button
                variant="ghost"
                className="ml-auto h-8 w-8 p-0"
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <Loader className="h-5 w-5 animate-spin text-slate-500" />
                ) : (
                  <MoreHorizontal className="h-5 w-5 text-slate-500" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white"
              onClick={(event) => event.stopPropagation()}
            >
              {onShowActivities && (
                <DropdownMenuItem
                  className="font-sans cursor-pointer"
                  onClick={() => onShowActivities(row)}
                >
                  <Activity className="mr-2 h-4 w-4" />
                  Atividades
                </DropdownMenuItem>
              )}
              {canApprove && (
                <DropdownMenuItem
                  className="font-sans cursor-pointer"
                  disabled={
                    actionLoading || Boolean(row.approvedAt) || isBilled
                  }
                  onClick={() => onApprove?.(row.id)}
                >
                  Validar
                </DropdownMenuItem>
              )}
              {canReject && (
                <DropdownMenuItem
                  className="font-sans cursor-pointer text-red-600 focus:text-red-600"
                  disabled={
                    actionLoading || Boolean(row.rejectedAt) || isBilled
                  }
                  onClick={() => onReject?.(row)}
                >
                  Invalidar
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    });

    const columns = [
      idViagem,
      shipment,
      notas,
      conhecimento,
      viagem,
      placa,
      agendamento,
      chegada,
      fimDescarregamento,
      permanenciaRec,
      fimCarregamento,
      saida,
      permanenciaExp,
      totalPermanencia,
      limiteRestante,
      estadiaPagar,
      valorEstadia,
      approvalProtocol,
      status,
    ];

    if (showApprovalStatus) {
      columns.push(approvalStatus);
    }

    if (showActions) {
      columns.push(actions);
    }

    return columns;
  }, [
    actionLoadingId,
    canApprove,
    canReject,
    onApprove,
    onReject,
    onShowActivities,
    showActions,
    showApprovalStatus,
  ]);
};
